import { FC, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { toast } from 'react-toastify';

import dayjs from 'dayjs';

import { IClient, IConsultation } from '../../../store/types';
import { getTime } from '../../../utils/getTime';
import { buildConsultationInviteText } from '../../../utils/buildConsultationInviteText';
import { useAppSelector } from '../../../hooks/redux';
import { selectUser } from '../../../store/auth.slice';
import { useGetSpecializationsQuery } from '../../../services/directus.api';
import { EditPencilIcon } from '../../icons/edit-pencil';
import { TrashIcon } from '../../icons/trash';

import Button from '../../common/button';
import ModalWrap from '../../common/modal-wrap';
import { navigateToJournalSession } from '../../../pages/journal/journal-navigation';

interface ConsultationHoverPopupProps {
  consultation: IConsultation;
  client?: Pick<IClient, 'id' | 'full_name' | 'time_zone'>;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onCopy?: () => void;
}

export const ConsultationHoverPopup: FC<ConsultationHoverPopupProps> = ({
  consultation,
  client,
  onClose,
  onEdit,
  onDelete,
  onCopy,
}) => {
  const navigate = useNavigate();
  const userData = useAppSelector(selectUser);
  const { data: specializationResponse } = useGetSpecializationsQuery();
  const [copy, setCopy] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (popupRef.current?.contains(target) || target.closest('[data-consultation-card="true"]')) {
        return;
      }
      onClose();
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [onClose]);

  useEffect(() => {
    if (copy) {
      const timer = setTimeout(() => {
        setCopy(false);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [copy]);

  const handleCopyLink = () => {
    if (!consultation.link) return;

    const defaultSpecialization = 'Психолог';
    const specializationItems = specializationResponse?.data ?? [];
    const selectedSpecialization = specializationItems.find(
      (option) => String(option.id) === String(userData?.specialization ?? ''),
    );
    const specializationText = selectedSpecialization?.title || defaultSpecialization;
    const psychNameFromUserFallback = [userData?.last_name, userData?.first_name, userData?.middle_name]
      .filter(Boolean)
      .join(' ');

    const text = buildConsultationInviteText(consultation, {
      specializationText,
      psychNameFromUserFallback,
      clientFullName: client?.full_name,
    });

    void navigator.clipboard.writeText(text).then(
      () => {
        setCopy(true);
        toast.success('Скопировано');
      },
      () => toast.error('Не удалось скопировать'),
    );
    onCopy?.();
  };

  const isGroupTherapy = consultation.is_group === true;

  const handleOpenTranscription = () => {
    navigateToJournalSession(navigate, consultation.id, consultation.client_entity, 'Транскрипция');
  };

  const title = client?.full_name || 'Встреча';
  const consultationDate = dayjs(consultation.date).format('DD.MM.YYYY');
  const timeStart = getTime(new Date(consultation.datetime_start));
  const timeEnd = getTime(new Date(consultation.datetime_end));
  const sessionNumber = consultation.consultation_number || 1;

  // Извлекаем короткую ссылку из полной ссылки
  const shortLink = consultation.link ? consultation.link.replace(/^https?:\/\//, '').replace(/\/$/, '') : '';

  return (
    <ModalWrap isShow={true}>
      <div
        ref={popupRef}
        className="w-full max-w-[600px] bg-[#F5F6FA] rounded-[20px] p-6 shadow-[1px_1px_12px_0px_rgba(9,88,146,0.16)] pointer-events-auto"
        style={{ boxShadow: '1px 1px 12px 0px rgba(9, 88, 146, 0.16)' }}
      >
        {/* Контент */}
        <div className="flex flex-col gap-6">
          {/* Заголовок и кнопки на одной линии */}
          <div className="flex items-center justify-between gap-4 min-h-0">
            <h2 className="font-comfortaa text-2xl font-semibold text-[#2C3038] flex-1 min-w-0 truncate pr-2">
              {title}
            </h2>
            <div className="flex items-center gap-4 flex-shrink-0">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
                className="w-7 h-7 flex items-center justify-center cursor-pointer group"
                aria-label="Редактировать"
              >
                <EditPencilIcon width={28} height={28} stroke="#737478" className="group-hover:stroke-[#C00000]" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                className="w-7 h-7 flex items-center justify-center cursor-pointer group"
                aria-label="Удалить"
              >
                <TrashIcon width={28} height={28} stroke="#737478" className="group-hover:stroke-[#C00000]" />
              </button>
              <button
                onClick={onClose}
                className="w-7 h-7 flex items-center justify-center cursor-pointer"
                aria-label="Закрыть"
              >
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M6.17472 6.17374C6.63033 5.71813 7.36902 5.71813 7.82463 6.17374L13.9997 12.3488L20.1747 6.17374C20.6303 5.71813 21.369 5.71813 21.8246 6.17374C22.2802 6.62935 22.2802 7.36804 21.8246 7.82366L15.6496 13.9987L21.8246 20.1737C22.2802 20.6294 22.2802 21.368 21.8246 21.8237C21.369 22.2793 20.6303 22.2793 20.1747 21.8237L13.9997 15.6486L7.82463 21.8237C7.36902 22.2793 6.63033 22.2793 6.17472 21.8237C5.7191 21.368 5.7191 20.6294 6.17472 20.1737L12.3498 13.9987L6.17472 7.82366C5.7191 7.36804 5.7191 6.62935 6.17472 6.17374Z"
                    fill="#2C3038"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Параметры */}
          <div className="flex flex-col gap-3">
            {/* Дата */}
            <div className="flex items-center gap-2">
              <span className="font-manrope text-base font-semibold text-[#2C3038]">Дата:</span>
              <span className="font-manrope text-base font-normal text-[#2C3038]">{consultationDate}</span>
            </div>

            {/* Время */}
            <div className="flex items-center gap-2">
              <span className="font-manrope text-base font-semibold text-[#2C3038]">Время:</span>
              <span className="font-manrope text-base font-normal text-[#2C3038]">
                {timeStart} - {timeEnd}
              </span>
            </div>

            {/* Название группы (если групповая терапия) */}
            {isGroupTherapy && (
              <div className="flex items-center gap-2">
                <span className="font-manrope text-base font-semibold text-[#2C3038]">Название группы:</span>
                <span className="font-manrope text-base font-normal text-[#2C3038]">{title}</span>
              </div>
            )}

            {/* Сессия */}
            <div className="flex items-center gap-2">
              <span className="font-manrope text-base font-semibold text-[#2C3038]">Сессия</span>
              <span className="font-manrope text-base font-normal text-[#2C3038]">№{sessionNumber}</span>
            </div>
          </div>

          {/* Ссылка */}
          <div className="flex items-center gap-2">
            <span className="font-manrope text-base font-semibold text-[#2C3038]">Поделиться ссылкой</span>
            <div className="flex items-center gap-2">
              <span className="font-manrope text-base font-normal text-[#CC0000]">{shortLink}</span>
              <button
                onClick={handleCopyLink}
                className="w-6 h-6 flex items-center justify-center cursor-pointer"
                aria-label="Копировать ссылку"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M6 11C6 8.172 6 6.757 6.879 5.879C7.757 5 9.172 5 12 5H15C17.828 5 19.243 5 20.121 5.879C21 6.757 21 8.172 21 11V16C21 18.828 21 20.243 20.121 21.121C19.243 22 17.828 22 15 22H12C9.172 22 7.757 22 6.879 21.121C6 20.243 6 18.828 6 16V11Z"
                    stroke="#CC0000"
                    strokeWidth="1.5"
                  />
                  <path
                    d="M6 19C5.20435 19 4.44129 18.6839 3.87868 18.1213C3.31607 17.5587 3 16.7956 3 16V10C3 6.229 3 4.343 4.172 3.172C5.343 2 7.229 2 11 2H15C15.7956 2 16.5587 2.31607 17.1213 2.87868C17.6839 3.44129 18 4.20435 18 5"
                    stroke="#CC0000"
                    strokeWidth="1.5"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Кнопка подключения справа внизу */}
          {consultation.consultation_type === 'offline' && consultation.lifecycle_status === 'ready' ? (
            <div className="flex justify-end">
              <Button
                type="black"
                onClick={handleOpenTranscription}
                className="!text-base !p-0 h-[38px] w-[270px] !min-w-[270px] flex items-center justify-center !font-semibold"
              >
                Открыть транскрипцию
              </Button>
            </div>
          ) : consultation.consultation_type !== 'offline' ? (
            <div className="flex justify-end">
              <Button
                type="black"
                onClick={() => consultation?.code && window.open(`/meet/${consultation.code}`, '_blank')}
                className="!text-base !p-0 h-[38px] w-[270px] !min-w-[270px] flex items-center justify-center !font-semibold"
              >
                Подключиться к встрече
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    </ModalWrap>
  );
};
